import React, { useState, useEffect } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { Copyright } from "../Copyright";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";

import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PhoneAndroidRoundedIcon from "@mui/icons-material/PhoneAndroidRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";

import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { logoutAction, updateSelectedCompanyAction } from "../redux/actions/login";
import { Drawer } from "../ui/Drawer";

const BRAND = '#3b95e8';

const NavItem = ({ icon: Icon, label, selected, disabled, onClick }) => (
  <ListItemButton selected={selected} disabled={disabled} onClick={onClick}
    sx={{ mx: 1, borderRadius: 2, mb: 0.25, px: 1.5, py: 0.9 }}
  >
    <ListItemIcon sx={{ minWidth: 36 }}>
      <Icon sx={{ fontSize: 19, color: selected ? BRAND : 'rgba(232,240,254,0.45)' }} />
    </ListItemIcon>
    <ListItemText
      primary={label}
      primaryTypographyProps={{
        fontSize: 13,
        fontWeight: selected ? 600 : 400,
        color: selected ? '#e8f0fe' : 'rgba(232,240,254,0.55)',
        letterSpacing: 0.2,
      }}
    />
  </ListItemButton>
);

const Protected = () => {
  const [open, setOpen] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user.user);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [isMobile]);

  const path = location.pathname;
  // A super admin sees only the admin tabs, until they pick a company: then
  // only that company's tabs, as its merchants see them (the cross in the
  // active company box brings the admin tabs back)
  const showAdminTabs = user.admin && !selectedCompany;
  const showCompanyTabs = !user.admin || !!selectedCompany;

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <CssBaseline />
      <Drawer variant="permanent" open={open}>

        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2.5, pb: 2 }}>
          <div onClick={() => setOpen(!open)} style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0, cursor: 'pointer',
            background: 'linear-gradient(135deg, #3b95e8 0%, #1a6fc4 100%)',
            boxShadow: '0 4px 16px rgba(59,149,232,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontFamily: 'FingerPaint, serif', fontSize: 24, color: '#fff', lineHeight: 1, marginTop: 2 }}>f</span>
          </div>
        </Box>

        {/* Commerce actif */}
        {selectedCompany && open && (
          <Box sx={{ mx: 1.5, mb: 1.5 }}>
            <Box sx={{
              px: 1.5, py: 1.2, borderRadius: 2,
              background: 'rgba(59,149,232,0.08)',
              border: '1px solid rgba(59,149,232,0.2)',
            }}>
              <Typography sx={{ color: 'rgba(232,240,254,0.35)', display: 'block', mb: 0.3, fontSize: 9, letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>
                Comercio activo
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ color: '#e8f0fe', fontWeight: 600, fontSize: 12 }} noWrap>
                  {selectedCompany.name}
                </Typography>
                {user.admin && (
                  <IconButton size="small" sx={{ color: 'rgba(232,240,254,0.35)', p: 0.3, flexShrink: 0 }}
                    onClick={() => { dispatch(updateSelectedCompanyAction(null)); navigate('/'); }}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Nav */}
        <List sx={{ px: 0, pt: 0.5 }} disablePadding>
          <NavItem icon={GridViewRoundedIcon} label="Dashboard" selected={path === "/"} onClick={() => navigate("/")} />
          {showAdminTabs && (
            <>
              <NavItem icon={ApartmentRoundedIcon} label="Empresas" selected={path === "/companies" || path === "/companies/edit"} onClick={() => navigate("/companies")} />
              <NavItem icon={ManageSearchRoundedIcon} label="Auditoría" selected={path === "/audit"} onClick={() => navigate("/audit")} />
              <NavItem icon={StarRoundedIcon} label="Valoraciones" selected={path === "/ratings"} onClick={() => navigate("/ratings")} />
              <NavItem icon={PhoneAndroidRoundedIcon} label="Versiones" selected={path === "/versions"} onClick={() => navigate("/versions")} />
              <NavItem icon={HistoryRoundedIcon} label="Conexiones" selected={path === "/connections"} onClick={() => navigate("/connections")} />
              <NavItem icon={ManageAccountsRoundedIcon} label="Cuentas web" selected={path === "/accounts"} onClick={() => navigate("/accounts")} />
            </>
          )}
          {showCompanyTabs && (
            <>
              <NavItem icon={TimelineRoundedIcon} label="Actividad" selected={path === "/activity"} disabled={!selectedCompany} onClick={() => navigate("/activity")} />
              <NavItem icon={ReportProblemRoundedIcon} label="Alertas" selected={path === "/alerts"} disabled={!selectedCompany} onClick={() => navigate("/alerts")} />
              <NavItem icon={EmojiEventsRoundedIcon} label="Estrategia" selected={path === "/strategy"} disabled={!selectedCompany} onClick={() => navigate("/strategy")} />
              <NavItem icon={GroupRoundedIcon} label="Usuarios" selected={path === "/users"} disabled={!selectedCompany} onClick={() => navigate("/users")} />
              <NavItem icon={StorefrontRoundedIcon} label="Locales" selected={path === "/locals" || path === "/locals/edit"} disabled={!selectedCompany} onClick={() => navigate("/locals")} />
              <NavItem icon={ForumRoundedIcon} label="Opiniones" selected={path === "/feedbacks"} disabled={!(selectedCompany && selectedCompany.gift_feedback)} onClick={() => navigate("/feedbacks")} />
              <NavItem icon={NotificationsNoneRoundedIcon} label="Notificaciones" selected={path === "/notifications"} disabled={!selectedCompany} onClick={() => navigate("/notifications")} />
            </>
          )}
        </List>

        <Box sx={{ flexGrow: 1 }} />

        {/* Logout */}
        <List sx={{ px: 0, pb: 1.5 }} disablePadding>
          <NavItem icon={PowerSettingsNewRoundedIcon} label="Cerrar sesión" onClick={() => dispatch(logoutAction())} />
        </List>

      </Drawer>

      {/* Main content */}
      <Box component="main" sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }}>
        <Box sx={{ mt: 4, mb: 4, px: 3, maxWidth: 1100, mx: 'auto' }}>
          <Outlet />
          <Copyright sx={{ pt: 4 }} />
        </Box>
      </Box>

      <ToastContainer position="bottom-right" />
    </Box>
  );
};

export default Protected;
